import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
  takeUntil,
} from 'rxjs';
import { OrganizationLogoService } from '../../../services/organization-logo.service';
import { LabelOrientation } from '../../../models_new/enums/label-orientation';
import { RosbagPlayerService } from '../../../services/rosbag/rosbag-player.service';
import { OrganizationApiService } from '../../../services/api/organization-api.service';
import * as THREE from 'three';
import { isUUIDv4 } from '../../../utils/div';
import { PlayerMode } from '../../gui/rosbag-player/rosbag-player.component';
import { PublicApiService } from 'src/app/services/api/public-api.service';
import { ISimvizLoadInfo } from 'src/app/models_new/types/simviz-load-info';
import { SimulationViewComponent } from '../../gui/3dview/views/simulation-view/simulation-view.component';
import { ISimulationMovementBox } from '../../../models_new/types/simulation-details';
import { IMovmentBoxOption } from 'src/app/models_new/types/3dview/rosbag-player-types';

@Component({
  selector: 'app-simviz',
  templateUrl: './simviz.component.html',
  styleUrls: ['./simviz.component.scss'],
  providers: [RosbagPlayerService],
})
export class SimvizComponent implements OnChanges, OnDestroy {
  destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() public: boolean = false;
  @Input() isOpen: boolean = false;

  @Input() loadinfo: ISimvizLoadInfo;
  @Input() productModelId: string;
  productModelId$ = new BehaviorSubject<string>(null); // Used to hold back the pipeline until ngOnInit has run.

  // The min and max dimensions the robot moved in while the simulation was running, if available.
  @Input() movementBoxSet: { [key: string]: ISimulationMovementBox };
  movementBoxOptions: IMovmentBoxOption[] = null;
  selectedMovementBoxes: ISimulationMovementBox[] = null;

  stickers$: Observable<{
    customerOrgWallSticker: THREE.Texture;
    salesOrgWallSticker: THREE.Texture;
    box: THREE.Texture;
  }>;

  @Input() labelOrientation: LabelOrientation;

  @Input() playerMode?: PlayerMode;
  @Output() playerModeEvent?: EventEmitter<PlayerMode> = new EventEmitter();

  ngOnChanges$ = new ReplaySubject<SimpleChanges>(1);

  @ViewChild(SimulationViewComponent) simulationView: SimulationViewComponent;

  mrcLogo: string;
  pallyLogo: string;

  constructor(
    public logoService: OrganizationLogoService,
    public organizationApi: OrganizationApiService,
    private publicApi: PublicApiService
  ) {
    this.mrcLogo = this.publicApi.mrcLogoBlue;
    this.pallyLogo = this.publicApi.pallyLogoBlue;

    this.stickers$ = this.ngOnChanges$.pipe(
      filter((changes: SimpleChanges) => {
        return Boolean(changes.loadinfo);
      }),
      map((changes: SimpleChanges) => changes.loadinfo.currentValue),
      switchMap((info) => {
        // No info, default to mrc logo.
        if (!info.customerOrgStickerSource) {
          info.customerOrgStickerSource = this.mrcLogo;
        }

        // No info, default to pally logo.
        if (!info.salesOrgStickerSource) {
          info.salesOrgStickerSource = this.pallyLogo;
        }

        // No info, default to label.
        if (!info.boxStickerSource) {
          info.boxStickerSource = '../../../../assets/illustrative/label.png';
        }

        return forkJoin({
          customerOrgWallStickerSource: isUUIDv4(info.customerOrgStickerSource)
            ? // Source is a uuid, fetch the asset.
              this.organizationApi.getOrganizationLogoUrl(
                info.customerOrganizationId
              )
            : // Wasn't an id, assume it's an URL.
              of(info.customerOrgStickerSource),

          salesOrgWallStickerSource: isUUIDv4(info.salesOrgStickerSource)
            ? // Source is a uuid, fetch the asset.
              this.organizationApi.getOrganizationLogoUrl(
                info.salesOrganizationId
              )
            : // Wasn't an id, assume it's an URL.
              of(info.salesOrgStickerSource),

          boxSource: isUUIDv4(info.boxStickerSource)
            ? // Source is a uuid, fetch the asset.
              this.organizationApi.getOrganizationLogoUrl(info.organizationId)
            : // Wasn't an id, assume it's an URL.
              of(info.boxStickerSource),
        });
      }),
      catchError((error) => {
        console.error(error);
        // If something fails to load, default to mrc logo.
        return of({
          customerOrgWallStickerSource: this.mrcLogo,
          salesOrgWallStickerSource: this.pallyLogo,
          boxSource: '../../../../assets/illustrative/label.png',
        });
      }),
      switchMap(
        ({
          customerOrgWallStickerSource,
          salesOrgWallStickerSource,
          boxSource,
        }) => {
          const customerOrgWallTexturePromise = this.threeTextureFromImageURL(
            customerOrgWallStickerSource,
            this.mrcLogo
          );
          const salesOrgWallTexturePromise = this.threeTextureFromImageURL(
            salesOrgWallStickerSource,
            this.pallyLogo
          );
          const boxTexturePromise = this.threeTextureFromImageURL(
            boxSource,
            '../../../../assets/illustrative/label.png'
          );

          return forkJoin({
            customerOrgWallSticker: from(customerOrgWallTexturePromise),
            salesOrgWallSticker: from(salesOrgWallTexturePromise),
            box: from(boxTexturePromise),
          });
        }
      ),
      shareReplay({ bufferSize: 1, refCount: false }),
      takeUntil(this.destroy$)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.ngOnChanges$.next(changes);
    if (this.movementBoxSet && changes.movementBoxSet?.firstChange) {
      this.movementBoxOptions = Object.keys(this.movementBoxSet).map((key) => ({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1).replaceAll('_', ' '),
        isVisible: false,
      }));
    }
    if (changes.productModelId?.currentValue) {
      this.productModelId$.next(changes.productModelId.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  setSelectedMovementBox(event: IMovmentBoxOption[]): void {
    if (!event) this.selectedMovementBoxes = [];
    else {
      this.selectedMovementBoxes = Object.keys(this.movementBoxSet)
        .filter((key) => event.some((e) => e.key === key))
        .map((key) => this.movementBoxSet[key]);
    }
  }

  threeTextureFromImageURL(
    imageURL: string,
    fallbackURL: string
  ): Promise<THREE.Texture> {
    return new Promise<THREE.Texture>((resolve, reject) => {
      const texture = new THREE.Texture();
      texture.image = new Image();
      texture.image.crossOrigin = 'Anonymous';

      texture.image.onload = () => {
        texture.needsUpdate = true;
        resolve(texture);
      };

      // If the logo fails to load with the url given, attempt to load fallbackURL.
      // If fallbackURL fails to load, we reject the promise.
      texture.image.onerror = (error) => {
        console.debug(`Couldn't load logo with URL: ${texture.image?.src}`);
        if (texture.image.src === fallbackURL) {
          reject(error);
          return;
        }
        texture.image.src = fallbackURL;
      };
      texture.image.src = imageURL;
    });
  }
}
